/**
 * MSAL (Microsoft Authentication Library) configuration for ACE Core Chemistry.
 *
 * Pattern mirrors JobQ2 / PlanAI: a single PublicClientApplication, redirect
 * flow (no popups — avoids COOP issues), sessionStorage cache. The access token
 * acquired here is POSTed to the backend (/api/v1/auth/microsoft) which mints
 * the app JWT we actually use for API calls.
 *
 * Env:
 * - VITE_MSAL_CLIENT_ID  — Azure AD app (client) id
 * - VITE_MSAL_TENANT_ID  — tenant id, or 'common' (default) for multi-tenant +
 *                          personal Microsoft accounts.
 */
import { Configuration, PublicClientApplication, LogLevel } from '@azure/msal-browser';

const clientId = import.meta.env.VITE_MSAL_CLIENT_ID || '';
const tenant = import.meta.env.VITE_MSAL_TENANT_ID || 'common';

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenant}`,
    redirectUri: `${window.location.origin}/auth/microsoft/callback`,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[MSAL]', message);
      },
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
};

// Scopes requested at login. openid/profile/email guarantee an id_token is
// issued — the backend VERIFIES the id_token (not the access token) to prevent
// token-substitution. User.Read lets it read the basic profile.
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const isMsalConfigured = (): boolean => !!clientId;
